import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle } from 'lucide-react';
import { createCustomer, Customer } from '../lib/customers';
import GoogleMapsLoader from './GoogleMapsLoader';

interface FormData {
  type: 'person' | 'business';
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  lat?: number;
  lng?: number;
}

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
  businessId: string;
  initialData?: Partial<FormData>;
}

const isFormValid = (formData: FormData) => {
  // Check if all required fields are filled
  const hasFirstName = formData.first_name.trim() !== '';
  const hasLastName = formData.last_name.trim() !== '';
  const hasPhone = formData.phone.trim() !== '';

  return (
    hasFirstName && 
    hasLastName && 
    hasPhone
  );
};

function CustomerModalInner({ isOpen, onClose, onSuccess, businessId, initialData }: CustomerModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    type: 'person',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    lat: undefined,
    lng: undefined
  });

  // Address autocomplete refs and state
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize autocomplete when modal opens and Google Maps API is loaded
  useEffect(() => {
    if (!isOpen || !addressInputRef.current) return;
    
    const checkAndInitialize = () => {
      if (window.google?.maps?.places && addressInputRef.current) {
        initializeAutocomplete();
      } else {
        console.log('Google Maps Autocomplete: Waiting for Google Maps API to load...');
        const timer = setTimeout(checkAndInitialize, 500);
        return () => clearTimeout(timer);
      }
    };

    checkAndInitialize();
    
    // Cleanup on unmount or when modal closes
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      setInitialized(false);
    };
  }, [isOpen]); // Re-initialize when modal opens

  // Handle Google autocomplete dropdown positioning
  useEffect(() => {
    if (!isOpen || !addressInputRef.current || !initialized) return;

    const updateDropdownPosition = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach((pacContainer: Element) => {
        const htmlPacContainer = pacContainer as HTMLElement;
        if (addressInputRef.current && htmlPacContainer.style.display !== 'none') {
          const rect = addressInputRef.current.getBoundingClientRect();
          htmlPacContainer.style.cssText = `
            position: fixed !important;
            top: ${rect.bottom}px !important;
            left: ${rect.left}px !important;
            width: ${rect.width}px !important;
            z-index: 200000 !important;
            transform: none !important;
          `;
        }
      });
    };

    // Update position on scroll and resize
    const handleScroll = () => updateDropdownPosition();
    window.addEventListener('scroll', handleScroll, true); // Use capture phase
    window.addEventListener('resize', updateDropdownPosition);

    // Monitor for dropdown appearance
    const observer = new MutationObserver(() => {
      updateDropdownPosition();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateDropdownPosition);
      observer.disconnect();
    };
  }, [isOpen, initialized]); // Re-setup when modal opens/closes or autocomplete initializes

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Set initial data if provided
      if (initialData) {
        setFormData(prev => ({
          ...prev,
          first_name: initialData.first_name || '',
          last_name: initialData.last_name || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          address: initialData.address || '',
          notes: initialData.notes || ''
        }));
      }
    }
  }, [isOpen, initialData]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset form data when modal closes
      setFormData({
        type: 'person',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        lat: undefined,
        lng: undefined,
        notes: ''
      });
      setError(null);
    }
  };

  // Function to initialize the autocomplete
  const initializeAutocomplete = () => {
    if (!addressInputRef.current) {
      console.error('Google Maps Autocomplete: Input ref not ready');
      return;
    }

    if (!window.google?.maps?.places) {
      console.error('Google Maps Autocomplete: Google Maps Places API not loaded');
      return;
    }

    try {
      console.log('Google Maps Autocomplete: Initializing autocomplete instance');

      // Clear any previous instances
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      
      const options: google.maps.places.AutocompleteOptions = {
        componentRestrictions: { country: "ca" },
        fields: [
          "address_components",
          "formatted_address",
          "geometry",
          "place_id"
        ],
        types: ["address"]
      };

      // Create new instance
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        options
      );

      console.log('Google Maps Autocomplete: Instance created with options:', options);

      // Add the place_changed listener
      if (autocompleteRef.current) {
        autocompleteRef.current.addListener("place_changed", handlePlaceSelect);
      }

      setInitialized(true);
      console.log('Google Maps Autocomplete: Initialization complete');
    } catch (error) {
      console.error('Google Maps Autocomplete: Error initializing:', error);
    }
  };

  // Handle place selection
  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) {
      console.error('Google Maps Autocomplete: No autocomplete instance');
      return;
    }

    const place = autocompleteRef.current.getPlace();
    console.log('Google Maps Autocomplete: Place selected:', place);

    if (!place.place_id) {
      console.error('Google Maps Autocomplete: No place_id in selected place');
      return;
    }

    // Process the selected place
    processPlace(place.place_id, place.formatted_address || '');
  };

  // Process a selected place with all address components
  const processPlace = (placeId: string, description: string) => {
    console.log('processPlace called with placeId:', placeId, 'description:', description);
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    
    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address', 'geometry']
      },
      (place, status) => {
        console.log('getDetails response - status:', status, 'place:', place);
        if (status === google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
          // Extract all address components
          const components: { [key: string]: string } = {};
          place.address_components.forEach(component => {
            component.types.forEach(type => {
              components[type] = component.long_name;
              components[type + '_short'] = component.short_name;
            });
          });

          // Build address parts
          const streetNumber = components['street_number'] || '';
          const route = components['route'] || '';
          const city = components['locality'] || components['postal_town'] || '';
          const province = components['administrative_area_level_1_short'] || components['administrative_area_level_1'] || '';
          const postalCode = components['postal_code'] || '';

          // Build the street address
          const street = `${streetNumber} ${route}`.trim();
          
          // Build formatted address ensuring postal code is included
          const formattedParts = [
            street,
            city,
            province,
            postalCode,
            'Canada'
          ].filter(Boolean);

          const formattedAddress = formattedParts.join(', ');

          // Extract coordinates from geometry
          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();

          console.log('Google Maps Autocomplete: Final address data:', { 
            formattedAddress, 
            lat, 
            lng, 
            postalCode,
            street,
            city,
            province
          });
          
          // Update the form data with full address including postal code
          setFormData(prev => ({
            ...prev,
            address: formattedAddress,
            lat,
            lng
          }));
        }
      }
    );
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const newCustomer = await createCustomer({
        business_id: businessId,
        ...formData
      } as any);

      onSuccess(newCustomer);
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100030]`}
      onClick={handleClose}
      style={{
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Create Customer
              </h2>
              <button 
                onClick={handleClose} 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Customer Information Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Customer Information</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Enter first name"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Enter last name"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email address (optional)"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <div className="relative">
                      <input
                        ref={addressInputRef}
                        type="text"
                        placeholder="Enter address (optional)"
                        value={formData.address}
                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoComplete="off"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="text-gray-400 text-sm" title={initialized ? "Google Maps Autocomplete Ready" : "Google Maps Not Loaded"}>
                          {initialized ? '✓' : '⚠️'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="bg-white p-6">
                <button
                  type="submit"
                  disabled={loading || !isFormValid(formData)}
                  className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors shadow-sm ${
                    isFormValid(formData) 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-gray-300 cursor-not-allowed'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Creating Customer...
                    </div>
                  ) : (
                    'Create Customer'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}

// Main component with Google Maps loader
export default function CustomerModal(props: CustomerModalProps) {
  // Read the API key from environment variables
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8";
  
  return (
    <GoogleMapsLoader apiKey={apiKey}>
      <CustomerModalInner {...props} />
    </GoogleMapsLoader>
  );
}